import { parse, stringify } from 'querystringify'

// Types
import { QueryObject, Song, State, Profile, Loop, TrackList } from 'types'

// Utils
import { COUNTRY } from '@constants'

export const queryObjectParams = [
  'artist', // (kitti): artist only here for artist page query selector to work
  'bpm_over',
  'bpm_under',
  'categories',
  'collection',
  'genres',
  'keys',
  'label',
  'ordering',
  'owned',
  'not_owned',
  'owner',
  'q',
  'regions',
  'limit',
  'release_year_after',
  'release_year_before',
  'size',
  'types',
  'browse',
  'id',
  'ids',
  'multitrack',
  'song',
]

export const selectQueryObjectFromSearchString = (search: string): QueryObject => {
  const {
    bpm_over,
    bpm_under,
    browse,
    categories,
    collection,
    genres,
    id,
    ids,
    keys,
    labels,
    not_owned,
    ordering,
    owned,
    owner,
    q,
    regions,
    release_year_after,
    release_year_before,
    types,
    multitrack,
    song,
  } = parse(search) as {
    [key: string]: string
  }
  let newQuery: QueryObject = {}
  if (bpm_over) {
    newQuery = { ...newQuery, bpm_over: +bpm_over }
  }
  if (bpm_under) {
    newQuery = { ...newQuery, bpm_under: +bpm_under }
  }
  if (browse) {
    newQuery = { ...newQuery, browse: +browse }
  }
  if (categories) {
    newQuery = {
      ...newQuery,
      categories: categories.split(',').map((category: string) => +category),
    }
  }
  if (collection) {
    newQuery = { ...newQuery, collection }
  }
  if (genres) {
    newQuery = {
      ...newQuery,
      genres: genres.split(',').map((genre: string) => genre),
    }
  }
  if (id) {
    newQuery = { ...newQuery, id: +id }
  }
  if (ids) {
    newQuery = { ...newQuery, ids }
  }

  if (keys) {
    newQuery = { ...newQuery, keys: keys.split(',').map((key: string) => key) }
  }
  if (labels) {
    newQuery = { ...newQuery, labels: labels.split(',').map((label: string) => +label) }
  }
  if (not_owned) {
    newQuery = { ...newQuery, not_owned: true }
  }
  if (ordering) {
    newQuery = { ...newQuery, ordering }
  }
  if (owned) {
    newQuery = { ...newQuery, owned: true }
  }
  if (owner) {
    newQuery = { ...newQuery, owner: +owner }
  }
  if (q) {
    newQuery = { ...newQuery, q }
  }
  if (multitrack) {
    newQuery = { ...newQuery, multitrack: true }
  }
  if (song) {
    newQuery = { ...newQuery, song }
  }
  if (regions) {
    newQuery = {
      ...newQuery,
      regions: regions.split(',').map((regionId: string) => +regionId),
    }
  }
  if (release_year_after) {
    newQuery = { ...newQuery, release_year_after: +release_year_after }
  }
  if (release_year_before) {
    newQuery = { ...newQuery, release_year_before: +release_year_before }
  }
  if (types) {
    newQuery = { ...newQuery, types: types.split(',').map((type: string) => +type) }
  }

  return newQuery
}

export const selectSearchToQuery = (state: State): QueryObject => {
  const { search } = state.router.location
  return selectQueryObjectFromSearchString(search)
}

export const selectPurgedSearchString = (search: string): string => {
  const queryObject = parse(search)
  const parsedSearch = stringify(
    queryObjectParams.reduce((agg, param) => {
      if (!queryObject[param as keyof typeof queryObject]) {
        return agg
      }
      return { ...agg, [String(param)]: queryObject[param as keyof typeof queryObject] }
    }, {}),
  )
  return parsedSearch
}

export const selectTrackIdsFromSearchQuery = (state: State, search: string): Array<number> => {
  const parsedSearch = selectPurgedSearchString(search)
  return state.browse.results[parsedSearch]?.ids || []
}

export const selectTrackListFromTrackIds = (state: State, trackList: Array<{ id: number; loop?: Loop | null }>): TrackList => {
  if (!trackList) {
    return []
  }
  const { tracks } = state.entities
  const filteredTrackList = trackList.map(({ id: trackId, loop }) => {
    const track = tracks[String(trackId)]
    return { trackId, songId: track?.song, loop }
  })

  return filteredTrackList
}

export const songIsCountryRestricted = (song: Song, profile: Profile): boolean =>
  song &&
  song.restricted_countries &&
  (song.restricted_countries.includes(COUNTRY) || (profile && song.restricted_countries.includes(profile.country)))

export const selectTrackListWithoutRegionLockedTracks = (state: State, trackList: TrackList): TrackList => {
  const { songs } = state.entities
  const { profileId } = state.auth
  const profile = state.entities.profiles[String(profileId)]

  return trackList.filter(x => {
    const song = Object.values(songs).find(s => s.id === x.songId) as Song
    return !songIsCountryRestricted(song, profile)
  })
}
